import { Dispatch, SetStateAction, useLayoutEffect, useState } from "react";
import { Box, Button, ClickAwayListener, Paper, Popper, Stack, useTheme } from "@mui/material";
import FileTree from "files/components/FileTree";
import FileInfo from "files/types/interfaces/fileInfo";

// Fixed heights for the FileSelect popper components (in pixels)
// Paper padding (16px top + 16px bottom) + gap (8px) + buttons row (~40px) + safety margin (16px)
const POPPER_CHROME_HEIGHT = 96;
const MIN_FILE_TREE_HEIGHT = 150;

const FileSelect = ({
  popperAnchorEl,
  handlePopperClose,
  selectedFile,
  setSelectedFile,
  onSelectFile,
  loading,
  maxHeight: propMaxHeight,
}: {
  popperAnchorEl: null | HTMLElement;
  handlePopperClose: () => void;
  selectedFile: null | FileInfo;
  setSelectedFile: Dispatch<SetStateAction<FileInfo | null>>;
  onSelectFile: () => void;
  loading: boolean;
  maxHeight?: number;
}) => {
  const theme = useTheme();
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState<number | undefined>(undefined);

  // Calculate available height from anchor to bottom of viewport
  useLayoutEffect(() => {
    if (popperAnchorEl) {
      const calculateMaxHeight = () => {
        const anchorRect = popperAnchorEl.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        // Space from bottom of anchor to bottom of viewport, minus chrome and margin
        const availableHeight = viewportHeight - anchorRect.bottom - POPPER_CHROME_HEIGHT;
        // Clamp between minimum and a reasonable maximum (400px default)
        const clampedHeight = Math.max(MIN_FILE_TREE_HEIGHT, Math.min(availableHeight, 400));
        setDynamicMaxHeight(clampedHeight);
      };

      calculateMaxHeight();
      window.addEventListener("resize", calculateMaxHeight);
      return () => window.removeEventListener("resize", calculateMaxHeight);
    }
  }, [popperAnchorEl]);

  const effectiveMaxHeight = propMaxHeight ?? dynamicMaxHeight;

  return (
    <Popper
      disablePortal
      anchorEl={popperAnchorEl}
      open={!!popperAnchorEl}
      placement="bottom-start"
      sx={{ width: "100%", zIndex: theme.zIndex.tooltip }}
    >
      <ClickAwayListener onClickAway={handlePopperClose}>
        <Paper
          sx={{
            boxShadow:
              "0px 1px 5px 0px #0000001F, 0px 2px 2px 0px #00000024, 0px 3px 1px -2px #00000033",
          }}
        >
          <Stack p={2} gap={1}>
            <FileTree
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              showFiles
              maxHeight={effectiveMaxHeight}
            />
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" onClick={handlePopperClose}>
                Cancel
              </Button>
              <Button
                disabled={!selectedFile}
                loading={loading}
                variant="contained"
                onClick={onSelectFile}
              >
                Select
              </Button>
            </Box>
          </Stack>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default FileSelect;
